import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AdminBusiness from "./AdminBusiness";
import AdminAuditLog from "./AdminAuditLog";
import AdminCustomers from "./AdminCustomers";
import AdminProjects from "./AdminProjects";
import AdminPayments from "./AdminPayments";
import AdminSupport from "./AdminSupport";
import AdminGallery from "./AdminGallery";
import AdminSettings from "./AdminSettings";
import AdminPainterApplications from "./AdminPainterApplications";
import AdminPaintAliases from "./AdminPaintAliases";
import PreflightBanner from "@/components/admin/PreflightBanner";

const STATUS_OPTIONS = ["new", "contacted", "quoted", "won", "lost"];

function MetricCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-brand-linen p-6">
      <div className="overline mb-2">{label}</div>
      <div className="font-serif text-3xl text-brand-ink tracking-tight">{value}</div>
      {sub && <div className="text-xs text-brand-muted mt-2">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("business");
  const [metrics, setMetrics] = useState(null);
  const [visMetrics, setVisMetrics] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [err, setErr] = useState("");

  const loadMetrics = useCallback(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/visualiser-metrics").then((r) => setVisMetrics(r.data)).catch(() => {});
  }, []);
  const loadQuotes = useCallback(() => {
    api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  }, []);
  const loadUsers = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  const loadTickets = useCallback(() => {
    api.get("/admin/support").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    loadMetrics();
    loadQuotes();
    loadUsers();
    loadTickets();
  }, [user, loadMetrics, loadQuotes, loadUsers, loadTickets]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      loadQuotes();
      loadMetrics();
      if (selectedQuote?.id === id) setSelectedQuote({ ...selectedQuote, status });
      toast.success(`Lead status updated to "${status}"`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(`Could not update status: ${msg}`);
    }
  };

  const openQuote = async (id) => {
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setSelectedQuote(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!user || user === false) return null;
  if (user.role !== "admin" && !user.is_owner) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <p className="text-brand-muted text-sm mb-6">This area is for ColourMatch staff only.</p>
        <a
          href="/projects"
          data-testid="admin-access-denied-back"
          className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white transition-colors"
        >
          Back to your projects
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="overline mb-3">Internal</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-8">Admin dashboard</h1>

      {/* Owner-only Deploy Preflight banner — pulses when action is required. */}
      <PreflightBanner />

      {err && <div className="mb-6 text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">{err}</div>}

      {/* Tabs */}
      <div className="grid grid-cols-3 md:grid-cols-12 gap-px bg-brand-linen border border-brand-linen mb-10" data-testid="admin-tabs">
        {[
          ["business", "Business"],
          ["customers", "Customers"],
          ["projects", "Projects"],
          ["payments", "Payments"],
          ["overview", "Overview"],
          ["visualiser", "Visualiser"],
          ["leads", "Leads"],
          ["users", "Users"],
          ["painter-applications", "Painter Apps"],
          ["membership-apps", "Membership Apps"],
          ["gallery", "Gallery"],
          ["paint-aliases", "Paint aliases"],
          ["support", "Support"],
          ["settings", "Settings"],
          ["audit-log", "Audit log"],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            data-testid={`admin-tab-${v}`}
            className={`p-4 text-sm ${tab === v ? "bg-brand-navy text-white" : "bg-white hover:bg-brand-linen/30"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "business" && <AdminBusiness />}

      {tab === "customers" && <AdminCustomers />}

      {tab === "projects" && <AdminProjects />}

      {tab === "payments" && <AdminPayments />}

      {tab === "painter-applications" && <AdminPainterApplications />}

      {tab === "membership-apps" && (
        <div className="p-6 text-center bg-brand-cream border border-brand-border rounded-xl">
          <p className="text-brand-navy font-semibold mb-3">Full Membership Applications experience</p>
          <p className="text-sm text-gray-600 mb-4">Open the dedicated queue with filters, decision panel and audit trail.</p>
          <a href="/admin/membership-applications" className="inline-block px-4 py-2 bg-brand-teal text-white text-sm font-semibold rounded-full hover:bg-brand-teal/90" data-testid="open-membership-apps">
            Open Membership Applications →
          </a>
        </div>
      )}

      {tab === "audit-log" && <AdminAuditLog />}

      {tab === "overview" && metrics && (
        <div data-testid="admin-overview">
          <div className="overline mb-4">Users</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <MetricCard label="Total users" value={metrics.total_users} />
            <MetricCard label="Customers" value={metrics.total_customers} />
            <MetricCard label="New today" value={metrics.new_users_today} />
            <MetricCard label="New this week" value={metrics.new_users_this_week} />
          </div>
          <div className="overline mb-4">Quote leads</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <MetricCard label="Total leads" value={metrics.total_quotes} />
            <MetricCard label="Today" value={metrics.quotes_today} />
            <MetricCard label="This week" value={metrics.quotes_this_week} />
            <MetricCard label="Gallery items" value={metrics.gallery_count} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-brand-linen p-6" data-testid="status-breakdown">
              <div className="overline mb-4">Lead status breakdown</div>
              {Object.entries(metrics.quotes_by_status).length === 0 ? (
                <p className="text-sm text-brand-muted">No leads yet.</p>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(metrics.quotes_by_status).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-sm">
                      <span className="capitalize">{k}</span><span className="font-serif">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-brand-linen p-6" data-testid="scope-breakdown">
              <div className="overline mb-4">By project scope</div>
              {Object.entries(metrics.quotes_by_scope).length === 0 ? (
                <p className="text-sm text-brand-muted">No leads yet.</p>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(metrics.quotes_by_scope).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-sm">
                      <span className="capitalize">{k}</span><span className="font-serif">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "visualiser" && visMetrics && (
        <div data-testid="admin-visualiser">
          <div className="overline mb-1">Revenue & conversions</div>
          {visMetrics.excluded_internal && (
            <div className="text-xs text-brand-muted mb-4" data-testid="admin-data-source-note">
              Customer-only view · excludes {visMetrics.internal_user_count || 0} internal (owner/admin) account{(visMetrics.internal_user_count || 0) === 1 ? "" : "s"} and all quick-test projects.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <MetricCard label="Total revenue" value={`$${visMetrics.revenue_total_aud.toLocaleString()}`} sub="AUD · live Stripe" />
            <MetricCard label="Paid unlocks" value={visMetrics.paid_unlocks} sub="live Stripe" />
            <MetricCard label="Conversion rate" value={`${visMetrics.conversion_rate_pct}%`} sub="unlocked / total projects · customers only" />
            <MetricCard label="Unlock price" value={`$${visMetrics.unlock_price_aud}`} sub="AUD · live from config" />
          </div>
          <div className="overline mb-4">AI usage</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <MetricCard label="Total projects" value={visMetrics.total_projects} sub="customers only" />
            <MetricCard label="Total renders" value={visMetrics.total_renders} sub={`${visMetrics.failed_renders} failed · customers only`} />
            <MetricCard
              label="AI cost (est.)"
              value={`$${visMetrics.ai_cost_total_aud.toLocaleString()}`}
              sub={`AUD · estimated at $${(visMetrics.ai_cost_per_render_aud ?? 0).toFixed(2)}/render · not actual Gemini billing`}
            />
            <MetricCard
              label="Margin (est.)"
              value={`$${Math.max(0, visMetrics.revenue_total_aud - visMetrics.ai_cost_total_aud).toLocaleString()}`}
              sub="Revenue − estimated AI cost only · excludes Stripe fees, refunds, hosting and support costs"
            />
          </div>
          <div className="text-xs text-brand-muted mb-10" data-testid="admin-estimates-disclaimer">
            <strong>Estimates only:</strong> AI cost is calculated from a configurable rate (<code>EST_COST_PER_RENDER_AUD</code>),
            not actual Gemini billing. Margin excludes Stripe fees, refunds, hosting and support costs.
          </div>
          {visMetrics.ratings && (
            <>
              <div className="overline mb-4">Render quality (user ratings)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <MetricCard label="Thumbs up" value={visMetrics.ratings.up} />
                <MetricCard label="Thumbs down" value={visMetrics.ratings.down} />
                <MetricCard label="Rated total" value={visMetrics.ratings.rated_total} />
                <MetricCard label="Satisfaction" value={visMetrics.ratings.satisfaction_pct !== null ? `${visMetrics.ratings.satisfaction_pct}%` : "—"} sub="thumbs up / rated" />
              </div>
              {visMetrics.ratings.by_surface?.length > 0 && (
                <div className="bg-white border border-brand-linen p-6 mb-10" data-testid="surface-satisfaction">
                  <div className="overline mb-4">Satisfaction by surface</div>
                  <ul className="space-y-2">
                    {visMetrics.ratings.by_surface.map((s) => (
                      <li key={s.surface} className="flex items-center justify-between text-sm">
                        <span className="capitalize">{s.surface.replace("_", " ")}</span>
                        <span className="flex items-center gap-3 text-xs text-brand-muted">
                          <span>👍 {s.up}</span>
                          <span>👎 {s.down}</span>
                          <span className="font-serif text-brand-ink">{s.pct !== null ? `${s.pct}%` : "—"}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-brand-linen p-6" data-testid="popular-colours">
              <div className="overline mb-4">Popular colours</div>
              {visMetrics.popular_colours.length === 0 ? (
                <p className="text-sm text-brand-muted">No renders yet.</p>
              ) : (
                <ul className="space-y-2">
                  {visMetrics.popular_colours.map((c) => (
                    <li key={c.hex + c.name} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-3">
                        <span className="swatch-chip w-5 h-5" style={{ background: c.hex }} />
                        <span>{c.name}</span>
                        <span className="text-brand-muted text-xs font-mono">{c.hex}</span>
                      </span>
                      <span className="font-serif">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-brand-linen p-6" data-testid="popular-surfaces">
              <div className="overline mb-4">Popular surfaces</div>
              {Object.keys(visMetrics.popular_surfaces).length === 0 ? (
                <p className="text-sm text-brand-muted">No renders yet.</p>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(visMetrics.popular_surfaces).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-sm">
                      <span className="capitalize">{k.replace("_", " ")}</span>
                      <span className="font-serif">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div data-testid="admin-leads">
          {quotes.length === 0 ? (
            <p className="text-brand-muted text-sm">No leads yet.</p>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-brand-linen">
                {quotes.map((q) => (
                  <button key={q.id} onClick={() => openQuote(q.id)}
                    data-testid={`lead-row-${q.id}`}
                    className={`w-full text-left grid grid-cols-12 gap-2 items-center p-4 border-b border-brand-linen last:border-b-0 hover:bg-brand-linen/20 ${selectedQuote?.id === q.id ? "bg-brand-linen/30" : ""}`}>
                    <div className="col-span-6 md:col-span-4">
                      <div className="text-sm text-brand-ink">{q.full_name}</div>
                      <div className="text-xs text-brand-muted">{q.email}</div>
                    </div>
                    <div className="col-span-6 md:col-span-3 text-xs">
                      <div className="capitalize">{q.project_scope} · {q.property_type}</div>
                      <div className="text-brand-muted">{q.bedrooms}bd · {q.bathrooms}ba</div>
                    </div>
                    <div className="col-span-6 md:col-span-3 text-xs">
                      {q.estimate_low ? `$${Number(q.estimate_low).toLocaleString()}–$${Number(q.estimate_high).toLocaleString()}` : "—"}
                    </div>
                    <div className="col-span-6 md:col-span-2 text-right">
                      <span className="inline-block text-[10px] px-2 py-1 bg-brand-linen/60 uppercase tracking-widest">{q.status}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="bg-white border border-brand-linen p-6" data-testid="lead-detail">
                {selectedQuote ? (
                  <>
                    <div className="overline mb-2">Lead detail</div>
                    <h3 className="font-serif text-2xl mb-1">{selectedQuote.full_name}</h3>
                    <p className="text-sm text-brand-muted">{selectedQuote.email} · {selectedQuote.phone}</p>
                    <p className="text-sm mt-2">{selectedQuote.address}{selectedQuote.suburb ? `, ${selectedQuote.suburb}` : ""}</p>

                    <div className="overline mt-6 mb-3">Project</div>
                    <ul className="text-sm space-y-1">
                      <li className="capitalize">Scope: {selectedQuote.project_scope}</li>
                      <li className="capitalize">Type: {selectedQuote.property_type} · {selectedQuote.stories} storey</li>
                      <li>{selectedQuote.bedrooms} bed · {selectedQuote.bathrooms} bath · {selectedQuote.living_areas} living</li>
                      <li className="capitalize">Condition: {selectedQuote.surface_condition}</li>
                      <li className="capitalize">Access: {selectedQuote.access_difficulty}{selectedQuote.scaffold_required ? " · scaffold required" : ""}</li>
                      <li>Surfaces: {selectedQuote.surfaces.join(", ") || "—"}</li>
                      {selectedQuote.preferred_timeframe && <li>Timeframe: {selectedQuote.preferred_timeframe}</li>}
                    </ul>
                    {selectedQuote.notes && (
                      <>
                        <div className="overline mt-6 mb-2">Notes</div>
                        <p className="text-sm leading-relaxed">{selectedQuote.notes}</p>
                      </>
                    )}
                    {selectedQuote.estimate_low && (
                      <>
                        <div className="overline mt-6 mb-2">Estimate</div>
                        <p className="font-serif text-2xl">${Number(selectedQuote.estimate_low).toLocaleString()} – ${Number(selectedQuote.estimate_high).toLocaleString()}</p>
                      </>
                    )}

                    {selectedQuote.photos?.length > 0 && (
                      <>
                        <div className="overline mt-6 mb-2">Photos ({selectedQuote.photos.length})</div>
                        <div className="grid grid-cols-3 gap-2">
                          {selectedQuote.photos.map((p, i) => (
                            <a key={`p-${i}-${p.data?.length || 0}`} href={p.data} target="_blank" rel="noreferrer">
                              <img src={p.data} alt="" className="w-full h-24 object-cover border border-brand-linen" />
                            </a>
                          ))}
                        </div>
                      </>
                    )}

                    <div className="overline mt-6 mb-2">Update lead status</div>
                    <select value={selectedQuote.status} onChange={(e) => updateStatus(selectedQuote.id, e.target.value)}
                      data-testid="lead-status-select"
                      className="w-full h-12 border-2 border-brand-teal rounded-lg px-3 py-2 text-base font-semibold text-brand-teal bg-white focus:outline-none focus:ring-2 focus:ring-brand-teal/30 cursor-pointer hover:bg-brand-teal/5 transition-colors">
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <p className="mt-2 text-[11px] text-brand-muted">Changes save automatically.</p>
                  </>
                ) : (
                  <p className="text-sm text-brand-muted">Select a lead to see details.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <div data-testid="admin-users">
          <div className="bg-white border border-brand-linen">
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-12 gap-3 items-center p-4 border-b border-brand-linen last:border-b-0" data-testid={`user-row-${u.id}`}>
                <div className="col-span-12 md:col-span-4">
                  <div className="text-sm">{u.name}</div>
                  <div className="text-xs text-brand-muted">{u.email}</div>
                </div>
                <div className="col-span-6 md:col-span-3 text-xs">{u.phone || "—"}</div>
                <div className="col-span-6 md:col-span-2 text-xs uppercase tracking-widest">{u.role}</div>
                <div className="col-span-12 md:col-span-3 text-xs text-brand-muted">{new Date(u.created_at).toLocaleDateString()}</div>
              </div>
            ))}
            {users.length === 0 && <p className="p-6 text-sm text-brand-muted">No users yet.</p>}
          </div>
        </div>
      )}

      {tab === "gallery" && <AdminGallery />}

      {tab === "paint-aliases" && <AdminPaintAliases />}

      {tab === "support" && <AdminSupport />}

      {tab === "settings" && <AdminSettings />}
    </div>
  );
}
